/**
 * Back-office — détail d'un partenaire : profil + revue des pièces de
 * conformité (valider / refuser avec motif).
 */
import type {
  PartnerComplianceDoc,
  PartnerComplianceDocType,
} from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  getComplianceDownloadUrl,
  getPartnerDetail,
  reviewComplianceDoc,
} from '@/features/partners/partners-api';

const docTypeLabels: Record<PartnerComplianceDocType, string> = {
  kbis: 'KBIS',
  attestation_urssaf: 'Attestation URSSAF',
  assurance_rc: 'Assurance RC pro',
  statuts: 'Statuts',
  rib: 'RIB',
  piece_identite_dirigeant: 'Pièce identité dirigeant',
  autre: 'Autre',
};

const docStatusBadge: Record<PartnerComplianceDoc['status'], { label: string; cls: string }> = {
  en_attente: { label: 'En attente', cls: 'bg-gray-100 text-gray-700' },
  valide: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
  refuse: { label: 'Refusé', cls: 'bg-red-100 text-red-800' },
  expire: { label: 'Expiré', cls: 'bg-orange-100 text-orange-800' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

export function AdminPartnerDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['admin', 'partner', id],
    queryFn: () => getPartnerDetail(id),
    enabled: Boolean(id),
  });

  const [reasons, setReasons] = useState<Record<string, string>>({});

  const reviewMut = useMutation({
    mutationFn: ({
      documentId,
      status,
      reason,
    }: {
      documentId: string;
      status: 'valide' | 'refuse';
      reason?: string;
    }) => reviewComplianceDoc(documentId, { status, reason }),
    onSuccess: () => {
      toast.success('Pièce mise à jour');
      qc.invalidateQueries({ queryKey: ['admin', 'partner', id] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function download(documentId: string) {
    try {
      const url = await getComplianceDownloadUrl(documentId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  const data = detail.data;

  return (
    <DashboardLayout variant="admin" title="Détail partenaire">
      <Button asChild variant="outline" size="sm" className="mb-4">
        <Link to="/admin/partners">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Link>
      </Button>

      {!data ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{data.profile?.companyName ?? 'Partenaire'}</CardTitle>
              <CardDescription>
                {data.user.firstName} {data.user.lastName} — {data.user.email}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <span className="text-gray-500">SIRET : </span>
                {data.profile?.siret ?? '—'}
              </div>
              <div>
                <span className="text-gray-500">Forme juridique : </span>
                {data.profile?.legalForm ?? '—'}
              </div>
              <div className="sm:col-span-2">
                <span className="text-gray-500">Adresse : </span>
                {data.profile?.address ?? '—'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pièces de conformité</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pièce</TableHead>
                    <TableHead>Validité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Revue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.complianceDocs.length ? (
                    data.complianceDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">
                          {docTypeLabels[doc.docType]}
                          <span className="block text-xs text-gray-500">
                            {doc.filename}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(doc.expiresAt)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={docStatusBadge[doc.status].cls}>
                            {docStatusBadge[doc.status].label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => download(doc.id)}
                              aria-label="Télécharger"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Input
                              placeholder="Motif"
                              className="h-8 w-32"
                              value={reasons[doc.id] ?? ''}
                              onChange={(e) =>
                                setReasons({ ...reasons, [doc.id]: e.target.value })
                              }
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-green-50 text-green-800"
                              disabled={reviewMut.isPending}
                              onClick={() =>
                                reviewMut.mutate({ documentId: doc.id, status: 'valide' })
                              }
                            >
                              Valider
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-red-50 text-red-800"
                              disabled={reviewMut.isPending || !reasons[doc.id]}
                              onClick={() =>
                                reviewMut.mutate({
                                  documentId: doc.id,
                                  status: 'refuse',
                                  reason: reasons[doc.id],
                                })
                              }
                            >
                              Refuser
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                        Aucune pièce déposée.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
